'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as React from 'react';
import { PropsWithChildren } from 'react';

import { cn } from '../../../libs/utils';
import { MainTitle, MainTitleProps } from '../../organisms';
import { BaseOverlay } from '../overlay/Overlay';

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay ref={ref} asChild {...props}>
      <BaseOverlay
        className={cn(
          'z-overlay-dialog data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className
        )}
      />
    </DialogPrimitive.Overlay>
  )
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  disableOutsideClick?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, disableOutsideClick, ...props }, ref) => {
    return (
      <DialogPortal>
        <DialogOverlay />
        <div className={'container-padding-large fixed inset-0 z-dialog flex items-center justify-center'}>
          <DialogPrimitive.Content
            aria-describedby={undefined}
            ref={ref}
            className={cn(
              'container-xsmall w-full overflow-hidden rounded-bzc-large bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              className
            )}
            onInteractOutside={disableOutsideClick ? (e) => e.preventDefault() : props.onInteractOutside}
            {...props}
          >
            <VisuallyHidden>
              <DialogPrimitive.DialogTitle />
            </VisuallyHidden>
            {children}
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

interface DialogTitleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, Pick<MainTitleProps, 'title' | 'description'> {}

const DialogTitle = ({ className, title, description, ...props }: DialogTitleProps) => (
  <div className={cn('px-normal-large', className)} {...props}>
    <MainTitle title={title} description={description} size={'small'} />
  </div>
);
DialogTitle.displayName = 'DialogTitle';

const DialogFooter = ({ className, children, ...props }: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) => (
  <div className={cn(className, 'pt-2')} {...props}>
    {children}
  </div>
);
DialogFooter.displayName = 'DialogFooter';

export { Dialog, DialogPortal, DialogOverlay, DialogContent, DialogFooter, DialogTitle };
